/**
 * VoitureHub — Page d'accueil du contenu voiture sur code2roues.fr.
 * Route : /voiture
 *
 * Refonte : affiche directement la Une + une grille d'articles + les 9 thèmes ETM,
 * plutôt que 2 simples cartes-compteurs.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Car,
  BookOpen,
  GraduationCap,
  ArrowRight,
  ExternalLink,
  Clock,
  Sparkles,
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const CAR_BLUE = '#1d4ed8';

// Fallback couleur si pas d'image
const FALLBACK_HERO =
  'https://images.pexels.com/photos/210182/pexels-photo-210182.jpeg?auto=compress&cs=tinysrgb&w=1600';

export default function VoitureHub() {
  const [hub, setHub] = useState(null);
  const [magazine, setMagazine] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Voiture · Code 2 Roues';
    Promise.all([
      axios.get(`${API}/api/voiture/hub`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/api/voiture/magazine`).then((r) => r.data?.items || []).catch(() => []),
      axios.get(`${API}/api/voiture/dossiers`).then((r) => r.data?.items || []).catch(() => []),
    ]).then(([h, m, d]) => {
      setHub(h);
      setMagazine(m);
      setDossiers(d);
      setLoading(false);
    });
  }, []);

  const counts = hub?.counts || {};
  const featured = magazine[0]; // La Une = article magazine le plus récent
  const rest = magazine.slice(1, 7); // 6 articles suivants

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="voiture-hub">
      {/* Bandeau contextuel compact */}
      <div
        className="rounded-2xl border-2 p-4 sm:p-5 mb-6 sm:mb-8 flex items-start gap-3 sm:gap-4"
        style={{ borderColor: CAR_BLUE, background: 'rgba(29, 78, 216, 0.05)' }}
      >
        <Car className="w-6 h-6 sm:w-7 sm:h-7 flex-shrink-0 mt-0.5" style={{ color: CAR_BLUE }} />
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] mb-1 flex items-center gap-2"
            style={{ color: CAR_BLUE }}
          >
            <Sparkles className="w-3 h-3" /> Nouveau · Espace Voiture
          </div>
          <h1
            className="text-xl sm:text-2xl font-black tracking-tight text-foreground leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Le code voiture, même ADN magazine.
          </h1>
          <p className="text-xs sm:text-sm text-foreground/70 mt-2 leading-relaxed">
            Contenu importé de <strong>codevoiture.fr</strong>, notre plateforme sœur. Même équipe
            ECSR, même format long-read, même gratuité 100 %.
          </p>
        </div>
      </div>

      {loading && (
        <div className="text-center py-16 text-foreground/50 text-sm" data-testid="voiture-loading">
          Chargement du contenu voiture…
        </div>
      )}

      {/* LA UNE (article vedette) */}
      {featured && (
        <Link
          to={`/voiture/magazine/${featured.slug}`}
          className="group block rounded-2xl overflow-hidden border-2 border-border hover:border-[#1d4ed8] transition-all mb-8 sm:mb-10 bg-card"
          data-testid="voiture-featured"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {/* Image */}
            <div
              className="aspect-video md:aspect-auto md:h-full min-h-[240px] bg-cover bg-center relative"
              style={{ backgroundImage: `url(${featured.heroImage || FALLBACK_HERO})` }}
            >
              <div
                className="absolute top-3 left-3 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest text-white"
                style={{ background: CAR_BLUE }}
              >
                À la Une · Voiture
              </div>
            </div>
            {/* Texte */}
            <div className="p-5 sm:p-7 flex flex-col justify-center">
              {featured.kicker && (
                <div
                  className="text-[10px] font-black uppercase tracking-[0.2em] mb-2"
                  style={{ color: CAR_BLUE }}
                >
                  {featured.kicker}
                </div>
              )}
              <h2
                className="text-2xl sm:text-3xl font-black leading-tight text-foreground group-hover:text-[#1d4ed8] transition-colors"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                {featured.title}
              </h2>
              {featured.teaser && (
                <p className="text-sm text-foreground/70 mt-3 line-clamp-3 leading-relaxed">
                  {featured.teaser}
                </p>
              )}
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-border/50">
                <span className="text-[11px] text-foreground/60 inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {featured.readTimeMin || 8} min de lecture
                </span>
                <span
                  className="ml-auto text-[11px] font-black inline-flex items-center gap-1 group-hover:translate-x-1 transition-transform"
                  style={{ color: CAR_BLUE }}
                >
                  Lire l&apos;enquête <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* GRILLE MAGAZINE */}
      {rest.length > 0 && (
        <section className="mb-10 sm:mb-12" data-testid="voiture-magazine-grid">
          <div className="flex items-baseline justify-between mb-4 sm:mb-5">
            <div>
              <div
                className="text-[10px] font-black uppercase tracking-[0.22em] mb-1"
                style={{ color: CAR_BLUE }}
              >
                Magazine long-read
              </div>
              <h3
                className="text-xl sm:text-2xl font-black tracking-tight text-foreground"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                {counts.enquetes_magazine || magazine.length} enquêtes voiture.
              </h3>
            </div>
            <Link
              to="/voiture/magazine"
              className="text-[11px] font-black inline-flex items-center gap-1 hover:gap-2 transition-all"
              style={{ color: CAR_BLUE }}
              data-testid="voiture-magazine-see-all"
            >
              Voir tout <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {rest.map((a) => (
              <Link
                key={a.slug}
                to={`/voiture/magazine/${a.slug}`}
                className="group rounded-xl overflow-hidden border-2 border-border hover:border-[#1d4ed8] bg-card transition-all hover:shadow-md flex flex-col"
                data-testid={`voiture-card-${a.slug}`}
              >
                <div
                  className="aspect-video bg-cover bg-center bg-muted"
                  style={{ backgroundImage: `url(${a.heroImage || FALLBACK_HERO})` }}
                />
                <div className="p-4 flex-1 flex flex-col">
                  {a.kicker && (
                    <div
                      className="text-[9px] font-black uppercase tracking-[0.18em] mb-1.5"
                      style={{ color: CAR_BLUE }}
                    >
                      {a.kicker}
                    </div>
                  )}
                  <h4
                    className="text-sm sm:text-base font-black leading-tight text-foreground group-hover:text-[#1d4ed8] transition-colors line-clamp-3"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {a.title}
                  </h4>
                  {a.teaser && (
                    <p className="text-xs text-foreground/60 mt-2 line-clamp-2 flex-1">
                      {a.teaser}
                    </p>
                  )}
                  <div className="mt-3 pt-3 border-t border-border/50 flex items-center justify-between">
                    <span className="text-[10px] text-foreground/50 inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {a.readTimeMin || 5} min
                    </span>
                    <span
                      className="text-[10px] font-black inline-flex items-center gap-0.5 group-hover:translate-x-0.5 transition-transform"
                      style={{ color: CAR_BLUE }}
                    >
                      Lire <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* THÈMES ETM (9 dossiers officiels) */}
      {dossiers.length > 0 && (
        <section className="mb-10" data-testid="voiture-dossiers-grid">
          <div className="mb-4 sm:mb-5">
            <div
              className="text-[10px] font-black uppercase tracking-[0.22em] mb-1"
              style={{ color: CAR_BLUE }}
            >
              Apprendre par thème
            </div>
            <h3
              className="text-xl sm:text-2xl font-black tracking-tight text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Les {dossiers.length} thèmes officiels du code voiture.
            </h3>
            <p className="text-xs sm:text-sm text-foreground/60 mt-1">
              + {counts.satellites || 0} articles satellites pour approfondir chaque thème.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
            {dossiers.map((d) => (
              <a
                key={d.slug || d.id}
                href={d.slug ? `https://codevoiture.fr/apprendre/${d.slug}` : 'https://codevoiture.fr'}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-lg border-2 border-border bg-card p-3 sm:p-4 hover:border-[#1d4ed8] hover:shadow-md transition-all block"
                data-testid={`voiture-dossier-${d.slug || d.themeNumber}`}
              >
                <div className="flex items-start gap-2">
                  <div
                    className="w-8 h-8 rounded-md flex items-center justify-center text-white flex-shrink-0 font-black text-xs"
                    style={{ background: CAR_BLUE }}
                  >
                    {d.themeNumber || <GraduationCap className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] font-black uppercase tracking-widest text-foreground/40 flex items-center gap-1">
                      Thème {d.themeNumber || '—'}
                      <ExternalLink className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <h5 className="text-xs sm:text-sm font-black leading-tight text-foreground group-hover:text-[#1d4ed8] mt-0.5 line-clamp-2 transition-colors">
                      {d.title || d.slug}
                    </h5>
                  </div>
                </div>
              </a>
            ))}
          </div>
          <p className="text-[10px] text-foreground/40 mt-3 italic text-center sm:text-left">
            ↗ Les thèmes ETM s&apos;ouvrent sur <strong>codevoiture.fr</strong> pour le moment.
            L&apos;intégration native arrive prochainement.
          </p>
        </section>
      )}

      {/* Fallback si rien du tout */}
      {!loading && magazine.length === 0 && dossiers.length === 0 && (
        <div
          className="rounded-xl border-2 border-dashed border-border p-8 text-center"
          data-testid="voiture-empty"
        >
          <BookOpen className="w-8 h-8 mx-auto mb-3 text-foreground/40" />
          <p className="text-sm text-foreground/60">
            Aucun contenu voiture disponible pour le moment. Revenez bientôt !
          </p>
        </div>
      )}

      {/* Retour espace moto */}
      <div className="mt-8 sm:mt-10 text-center border-t border-border/50 pt-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-foreground/60 hover:text-[hsl(var(--primary))]"
          data-testid="back-to-moto"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Retourner à l&apos;espace moto
        </Link>
      </div>
    </div>
  );
}
